import { useCallback, useMemo } from 'react';
import { STUDIO_THEMES, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { CatalogItem, SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { setPendingStudioParams } from '../../draftStore';
import { usePostKitAsset } from '../../library/usePostKitAsset';
import { useAccountId } from '../../../../../redux/slices/appInfo/hooks';
import { useOpenMusicVideoEditor } from '../../addedAssets/useOpenMusicVideoEditor';

export interface KitNavigation {
  /** "Make something": the Studio on this song. */
  openStudio: () => void;
  /** The Studio on one theme of this song ("Make an AI reel", "Open the reels"); the music video opens its editor. */
  openTheme: (themeId: StudioThemeId) => void;
  /**
   * "New version": the Studio on the theme. The details of this version wait in `draftStore`
   * (`takePendingStudioParams`) for the Studio to read.
   */
  newVersion: (asset: SerializedKitAsset) => void;
  /** "Post it": the Composer with this file. Show it only when `canPostKitAsset` is true. */
  postAsset: (asset: SerializedKitAsset) => void;
  openCatalog: () => void;
}

export function useKitNavigation(item: CatalogItem): KitNavigation {
  const router = useSMLRouter();
  const catalogItemUuid = item.uuid;

  const openStudio = useCallback(
    () => router.push(SMLRoutes.promoteStudio, { catalogItemUuid }),
    [catalogItemUuid, router]
  );

  // The music video is made in its editor, not in the Studio (spec 2026-10-09 D11).
  const openEditor = useOpenMusicVideoEditor(useAccountId());
  const openTheme = useCallback(
    (themeId: StudioThemeId) =>
      themeId === 'musicVideo'
        ? openEditor(catalogItemUuid)
        : router.push(SMLRoutes.promoteStudio, {
            catalogItemUuid,
            format: STUDIO_THEMES[themeId].format,
            theme: themeId,
          }),
    [catalogItemUuid, openEditor, router]
  );

  const newVersion = useCallback(
    (asset: SerializedKitAsset) => {
      setPendingStudioParams({ catalogItemUuid, themeId: asset.themeId, params: asset.params });
      openTheme(asset.themeId);
    },
    [catalogItemUuid, openTheme]
  );

  const postAsset = usePostKitAsset(item);
  const openCatalog = useCallback(() => router.push(SMLRoutes.catalog), [router]);

  return useMemo(
    () => ({ openStudio, openTheme, newVersion, postAsset, openCatalog }),
    [newVersion, openCatalog, openStudio, openTheme, postAsset]
  );
}
